/**
 * Branded types for semantic type safety in TypeScript.
 *
 * This module provides branded types that wrap primitives with semantic meaning,
 * preventing accidental misuse. For example, TenantId and UserId are both strings
 * (UUIDs), but they cannot be used interchangeably.
 *
 * Pattern:
 * - Each branded type uses intersection with a phantom brand property
 * - Constructor functions validate and brand values
 * - Type guards enable runtime type checking
 * - Zero runtime overhead, maximum compile-time safety
 *
 * @example
 * ```typescript
 * import { TenantId, tenantId, isTenantId } from '@/types/branded'
 *
 * // Create branded types
 * const tenant = tenantId("550e8400-e29b-41d4-a716-446655440000")
 *
 * // Type guards for runtime checks
 * if (isTenantId(someValue)) {
 *   // Guaranteed to be a valid TenantId
 *   processTenant(someValue)
 * }
 * ```
 */

// UUID pattern for validation
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Column name pattern (alphanumeric, underscore, no leading numbers)
const COLUMN_NAME_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/

// ============================================================================
// Branded UUID Types
// ============================================================================

/**
 * Branded UUID for tenant references.
 * Cannot be used interchangeably with other UUIDs.
 */
export type TenantId = string & { readonly __brand: 'TenantId' }

/**
 * Branded UUID for user references.
 * Cannot be used interchangeably with other UUIDs.
 */
export type UserId = string & { readonly __brand: 'UserId' }

/**
 * Branded UUID for ML model references.
 * Cannot be used interchangeably with other UUIDs.
 */
export type ModelId = string & { readonly __brand: 'ModelId' }

/**
 * Branded UUID for data source references.
 * Cannot be used interchangeably with other UUIDs.
 */
export type DataSourceId = string & { readonly __brand: 'DataSourceId' }

/**
 * Branded UUID for prediction references.
 * Cannot be used interchangeably with other UUIDs.
 */
export type PredictionId = string & { readonly __brand: 'PredictionId' }

/**
 * Branded UUID for model version references.
 * Cannot be used interchangeably with other UUIDs.
 */
export type VersionId = string & { readonly __brand: 'VersionId' }

/**
 * Branded UUID for webhook references.
 * Cannot be used interchangeably with other UUIDs.
 */
export type WebhookId = string & { readonly __brand: 'WebhookId' }

/**
 * Branded UUID for batch prediction references.
 * Cannot be used interchangeably with other UUIDs.
 */
export type BatchId = string & { readonly __brand: 'BatchId' }

/**
 * Branded UUID for notification references.
 * Cannot be used interchangeably with other UUIDs.
 */
export type NotificationId = string & { readonly __brand: 'NotificationId' }

/**
 * Branded UUID for A/B test references.
 * Cannot be used interchangeably with other UUIDs.
 */
export type ABTestId = string & { readonly __brand: 'ABTestId' }

/**
 * Validate that a string is a valid UUID.
 *
 * @param value - String to validate
 * @param typeName - Name of the branded type (for error messages)
 * @returns Validated UUID string
 * @throws Error if value is not a valid UUID
 */
function validateUuid(value: unknown, typeName: string): string {
  if (typeof value !== 'string') {
    throw new Error(`${typeName} must be a string, got ${typeof value}`)
  }

  if (!UUID_PATTERN.test(value)) {
    throw new Error(`Invalid ${typeName}: '${value}' is not a valid UUID`)
  }

  return value
}

/**
 * Create a validated TenantId.
 *
 * @param value - UUID string for tenant
 * @returns Branded TenantId
 * @throws Error if value is not a valid UUID
 *
 * @example
 * ```typescript
 * const tid = tenantId("550e8400-e29b-41d4-a716-446655440000")
 * // tenantId("invalid") // Throws Error
 * ```
 */
export function tenantId(value: string): TenantId {
  return validateUuid(value, 'TenantId') as TenantId
}

/**
 * Create a validated UserId.
 *
 * @param value - UUID string for user
 * @returns Branded UserId
 * @throws Error if value is not a valid UUID
 *
 * @example
 * ```typescript
 * const uid = userId("550e8400-e29b-41d4-a716-446655440000")
 * // userId("invalid") // Throws Error
 * ```
 */
export function userId(value: string): UserId {
  return validateUuid(value, 'UserId') as UserId
}

/**
 * Create a validated ModelId.
 *
 * @param value - UUID string for ML model
 * @returns Branded ModelId
 * @throws Error if value is not a valid UUID
 *
 * @example
 * ```typescript
 * const mid = modelId("550e8400-e29b-41d4-a716-446655440000")
 * // modelId("invalid") // Throws Error
 * ```
 */
export function modelId(value: string): ModelId {
  return validateUuid(value, 'ModelId') as ModelId
}

/**
 * Create a validated DataSourceId.
 *
 * @param value - UUID string for data source
 * @returns Branded DataSourceId
 * @throws Error if value is not a valid UUID
 */
export function dataSourceId(value: string): DataSourceId {
  return validateUuid(value, 'DataSourceId') as DataSourceId
}

/**
 * Create a validated PredictionId.
 *
 * @param value - UUID string for prediction
 * @returns Branded PredictionId
 * @throws Error if value is not a valid UUID
 */
export function predictionId(value: string): PredictionId {
  return validateUuid(value, 'PredictionId') as PredictionId
}

/**
 * Create a validated VersionId.
 *
 * @param value - UUID string for model version
 * @returns Branded VersionId
 * @throws Error if value is not a valid UUID
 */
export function versionId(value: string): VersionId {
  return validateUuid(value, 'VersionId') as VersionId
}

/**
 * Create a validated WebhookId.
 *
 * @param value - UUID string for webhook
 * @returns Branded WebhookId
 * @throws Error if value is not a valid UUID
 */
export function webhookId(value: string): WebhookId {
  return validateUuid(value, 'WebhookId') as WebhookId
}

/**
 * Create a validated BatchId.
 *
 * @param value - UUID string for batch prediction
 * @returns Branded BatchId
 * @throws Error if value is not a valid UUID
 */
export function batchId(value: string): BatchId {
  return validateUuid(value, 'BatchId') as BatchId
}

/**
 * Create a validated NotificationId.
 *
 * @param value - UUID string for notification
 * @returns Branded NotificationId
 * @throws Error if value is not a valid UUID
 */
export function notificationId(value: string): NotificationId {
  return validateUuid(value, 'NotificationId') as NotificationId
}

/**
 * Create a validated ABTestId.
 *
 * @param value - UUID string for A/B test
 * @returns Branded ABTestId
 * @throws Error if value is not a valid UUID
 */
export function abTestId(value: string): ABTestId {
  return validateUuid(value, 'ABTestId') as ABTestId
}

// Type guards for runtime checking

/**
 * Type guard to check if value is a valid TenantId.
 *
 * @param value - Value to check
 * @returns True if value is a valid TenantId
 *
 * @example
 * ```typescript
 * if (isTenantId(someValue)) {
 *   // Type checker knows this is TenantId
 *   processTenant(someValue)
 * }
 * ```
 */
export function isTenantId(value: unknown): value is TenantId {
  return typeof value === 'string' && UUID_PATTERN.test(value)
}

/**
 * Type guard to check if value is a valid UserId.
 *
 * @param value - Value to check
 * @returns True if value is a valid UserId
 */
export function isUserId(value: unknown): value is UserId {
  return typeof value === 'string' && UUID_PATTERN.test(value)
}

/**
 * Type guard to check if value is a valid ModelId.
 *
 * @param value - Value to check
 * @returns True if value is a valid ModelId
 */
export function isModelId(value: unknown): value is ModelId {
  return typeof value === 'string' && UUID_PATTERN.test(value)
}

/**
 * Type guard to check if value is a valid DataSourceId.
 */
export function isDataSourceId(value: unknown): value is DataSourceId {
  return typeof value === 'string' && UUID_PATTERN.test(value)
}

/**
 * Type guard to check if value is a valid PredictionId.
 */
export function isPredictionId(value: unknown): value is PredictionId {
  return typeof value === 'string' && UUID_PATTERN.test(value)
}

/**
 * Type guard to check if value is a valid VersionId.
 */
export function isVersionId(value: unknown): value is VersionId {
  return typeof value === 'string' && UUID_PATTERN.test(value)
}

/**
 * Type guard to check if value is a valid WebhookId.
 */
export function isWebhookId(value: unknown): value is WebhookId {
  return typeof value === 'string' && UUID_PATTERN.test(value)
}

/**
 * Type guard to check if value is a valid BatchId.
 */
export function isBatchId(value: unknown): value is BatchId {
  return typeof value === 'string' && UUID_PATTERN.test(value)
}

/**
 * Type guard to check if value is a valid NotificationId.
 */
export function isNotificationId(value: unknown): value is NotificationId {
  return typeof value === 'string' && UUID_PATTERN.test(value)
}

/**
 * Type guard to check if value is a valid ABTestId.
 */
export function isABTestId(value: unknown): value is ABTestId {
  return typeof value === 'string' && UUID_PATTERN.test(value)
}

// ============================================================================
// Branded String Types
// ============================================================================

/**
 * Branded string for validated column names.
 * Ensures SQL-safe identifiers.
 */
export type ColumnName = string & { readonly __brand: 'ColumnName' }

/**
 * Create a validated ColumnName.
 *
 * Column names must:
 * - Start with a letter or underscore
 * - Contain only letters, numbers, and underscores
 * - Not be empty
 * - Be reasonably short (max 255 chars)
 *
 * @param value - String to validate as column name
 * @returns Branded ColumnName
 * @throws Error if value is not a valid column name
 *
 * @example
 * ```typescript
 * const col = columnName("user_id")
 * const col2 = columnName("total_spent_usd")
 * // columnName("123invalid") // Throws Error
 * // columnName("has-dashes") // Throws Error
 * ```
 */
export function columnName(value: string): ColumnName {
  if (typeof value !== 'string') {
    throw new Error(`ColumnName must be a string, got ${typeof value}`)
  }

  if (!value) {
    throw new Error('ColumnName cannot be empty')
  }

  if (value.length > 255) {
    throw new Error(`ColumnName too long: ${value.length} chars (max 255)`)
  }

  if (!COLUMN_NAME_PATTERN.test(value)) {
    throw new Error(
      `Invalid ColumnName: '${value}' must start with letter/underscore ` +
        'and contain only letters, numbers, and underscores'
    )
  }

  return value as ColumnName
}

/**
 * Type guard to check if value is a valid ColumnName.
 *
 * @param value - Value to check
 * @returns True if value is a valid ColumnName
 */
export function isColumnName(value: unknown): value is ColumnName {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= 255 &&
    COLUMN_NAME_PATTERN.test(value)
  )
}

// ============================================================================
// Branded Array Types
// ============================================================================

/**
 * Branded array for ML feature vectors.
 * Ensures array contains only numeric values.
 */
export type FeatureVector = number[] & { readonly __brand: 'FeatureVector' }

/**
 * Create a validated FeatureVector.
 *
 * Feature vectors must:
 * - Contain only numeric values
 * - Have at least one element
 * - Not contain NaN or infinite values
 *
 * @param value - Array to convert to FeatureVector
 * @returns Branded FeatureVector
 * @throws Error if value is not a valid feature vector
 *
 * @example
 * ```typescript
 * const vec = featureVector([1.0, 2.5, 3.7])
 * // featureVector([]) // Throws Error
 * // featureVector([NaN]) // Throws Error
 * ```
 */
export function featureVector(value: number[]): FeatureVector {
  if (!Array.isArray(value)) {
    throw new Error(`FeatureVector must be an array, got ${typeof value}`)
  }

  if (value.length === 0) {
    throw new Error('FeatureVector cannot be empty')
  }

  for (let i = 0; i < value.length; i++) {
    const val = value[i]
    if (typeof val !== 'number') {
      throw new Error(`FeatureVector must contain only numbers, found ${typeof val} at index ${i}`)
    }
    if (Number.isNaN(val)) {
      throw new Error(`FeatureVector cannot contain NaN at index ${i}`)
    }
    if (!Number.isFinite(val)) {
      throw new Error(`FeatureVector cannot contain infinite values at index ${i}`)
    }
  }

  return value as FeatureVector
}

/**
 * Type guard to check if value is a valid FeatureVector.
 *
 * @param value - Value to check
 * @returns True if value is a valid FeatureVector
 */
export function isFeatureVector(value: unknown): value is FeatureVector {
  if (!Array.isArray(value) || value.length === 0) {
    return false
  }

  return value.every((val) => typeof val === 'number' && Number.isFinite(val))
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Extract the underlying primitive type from a branded type.
 *
 * @example
 * ```typescript
 * type Raw = Unbrand<TenantId> // string
 * ```
 */
export type Unbrand<T> = T extends string & { readonly __brand: string }
  ? string
  : T extends number & { readonly __brand: string }
    ? number
    : T extends unknown[] & { readonly __brand: string }
      ? unknown[]
      : T

/**
 * Convert a branded type back to its primitive form.
 * Use sparingly - only when interfacing with external APIs.
 *
 * @param value - Branded value to unbrand
 * @returns Raw primitive value
 *
 * @example
 * ```typescript
 * const tid = tenantId("550e8400-e29b-41d4-a716-446655440000")
 * const raw = unbrand(tid) // raw is a plain string
 * ```
 */
export function unbrand<T>(value: T): Unbrand<T> {
  return value as Unbrand<T>
}
